import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeField } from '@/components/dateFields';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '@/components/ui/Dialog';
import Button from '@/components/ui/Button';
import Stepper from '@/components/ui/Stepper';
import { Spinner } from '@/components/ui/Spinner';
import { AddSourceInput } from '../demoStore';
import { getSystemFixture } from '../fixtures/sourceCatalog';
import {
  CONNECTABLE_SYSTEMS, ConnectionMethod, SourceSystem, SyncFrequency, isPullMethod,
} from '../types';

// Two pulls and a push. The order runs from the most automatic to the most constrained, which is
// also the order an administrator can rule them out in.
const METHODS: ConnectionMethod[] = ['apiPull', 'fileFeed', 'webhookPush'];

const FREQUENCIES: SyncFrequency[] = ['hourly', 'daily', 'weekly'];

/**
 * Shown greyed in the empty Client ID field. Unlike the tenant address it is not pre-filled, so this
 * is the only thing telling an administrator what shape of value belongs there before they go and
 * ask for one.
 */
const EXAMPLE_CLIENT_ID = 'mowazaf-readonly-client';

/** The endpoint Mowazaf would issue for a push connection, shown so the customer can register it. */
function buildCallbackUrl(displayName: string): string {
  const slug = displayName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `https://app.mowazaf.com/api/integrations/inbound/${slug || 'source'}`;
}

interface AddSourceWizardProps {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onAdd: (input: AddSourceInput) => Promise<void>;
}

const AddSourceWizard: React.FC<AddSourceWizardProps> = ({ open, busy, onCancel, onAdd }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [system, setSystem] = useState<SourceSystem>('sapSuccessFactors');
  const [displayName, setDisplayName] = useState('');
  const [method, setMethod] = useState<ConnectionMethod>('apiPull');
  const [syncFrequency, setSyncFrequency] = useState<SyncFrequency>('daily');
  const [syncTimeOfDay, setSyncTimeOfDay] = useState('02:00');
  const [requiresSignedExchange, setRequiresSignedExchange] = useState(false);
  const [tenantUrl, setTenantUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [fileLocation, setFileLocation] = useState('');

  const steps = [
    { label: t('extInt.wizard.step.system') },
    { label: t('extInt.wizard.step.method') },
    { label: t('extInt.wizard.step.credentials') },
    { label: t('extInt.wizard.step.test') },
  ];

  const isPull = isPullMethod(method);
  // Only a daily or weekly run has a time of day to pick; "every hour" has none.
  const hasTimeOfDay = isPull && syncFrequency !== 'hourly';
  const callbackUrl = buildCallbackUrl(displayName);

  const chooseSystem = (next: SourceSystem) => {
    setSystem(next);
    setDisplayName(t(`extInt.system.${next}`) as string);
    setTenantUrl(getSystemFixture(next)?.defaultTenantUrl ?? '');
  };

  // Each transport asks for different things, so each has its own idea of "filled in". A file feed
  // has no client id to give, and demanding one was the wizard asking API questions of a customer
  // who chose the file route precisely because they have no API.
  const credentialsComplete =
    method === 'fileFeed'
      ? fileLocation.trim().length > 0
      : method === 'webhookPush'
        ? tenantUrl.trim().length > 0
        : tenantUrl.trim().length > 0 && clientId.trim().length > 0;

  const canAdvance = step === 0
    ? displayName.trim().length > 0
    : step === 2
      ? credentialsComplete
      : true;

  const handleFinish = async () => {
    await onAdd({
      system,
      displayName,
      method,
      tenantUrl,
      clientId: method === 'apiPull' ? clientId : '',
      fileLocation: method === 'fileFeed' ? fileLocation : undefined,
      callbackUrl: method === 'webhookPush' ? callbackUrl : undefined,
      syncFrequency: isPull ? syncFrequency : undefined,
      syncTimeOfDay: hasTimeOfDay ? syncTimeOfDay : undefined,
      requiresSignedExchange,
    });
    setStep(0);
  };

  const freshnessSummary = isPull
    ? t(`extInt.wizard.freshness.${syncFrequency}`)
    : t('extInt.wizard.freshness.push');

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <DialogContent
        className="max-w-2xl"
        data-testid="add-source-wizard"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t('extInt.wizard.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stepper steps={steps} activeStep={step} orientation="horizontal" />

          {step === 0 && (
            <div className="mt-4 space-y-3">
              <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground" data-testid="wizard-intro">
                {t('extInt.wizard.intro')}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {CONNECTABLE_SYSTEMS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => chooseSystem(s)}
                    className={`rounded-md border p-3 text-start transition-colors ${
                      system === s ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-foreground/30'
                    }`}
                    data-testid={`wizard-system-${s}`}
                  >
                    <span className="block text-sm font-medium">{t(`extInt.system.${s}`)}</span>
                  </button>
                ))}
              </div>
              {/* No named "coming soon" tiles. Which vendors follow is undecided, and a named tile
                  reads as a commitment. This says more are coming without naming one. */}
              <p className="text-xs text-muted-foreground" data-testid="wizard-more-systems">
                {t('extInt.wizard.moreSystems')}
              </p>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-foreground">{t('extInt.wizard.displayName')}</span>
                <input
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  data-testid="wizard-display-name"
                />
                <span className="mt-1 block text-xs text-muted-foreground">{t('extInt.wizard.displayNameHint')}</span>
              </label>
            </div>
          )}

          {step === 1 && (
            <div className="mt-4 space-y-4">
              <div className="grid gap-2">
                {METHODS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMethod(m)}
                    className={`rounded-md border p-3 text-start transition-colors ${
                      method === m ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-foreground/30'
                    }`}
                    data-testid={`wizard-method-${m}`}
                  >
                    <span className="block text-sm font-medium">{t(`extInt.method.${m}`)}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{t(`extInt.wizard.method.${m}.body`)}</span>
                    {/* What to have ready. The card above says what the method IS; an administrator
                        setting one up for the first time needs to know what it will ask them for. */}
                    <span className="mt-1.5 block text-xs font-medium text-foreground">
                      {t(`extInt.wizard.method.${m}.needs`)}
                    </span>
                  </button>
                ))}
              </div>

              {/* Frequency belongs to the transport, not to the credentials, and every pull has one.
                  Asking it only of the file feed left "how current is the API data?" unanswered on
                  the screen that is supposed to answer exactly that. */}
              {isPull ? (
                <fieldset className="rounded-md border border-border p-3" data-testid="wizard-schedule">
                  <legend className="px-1 text-sm font-medium text-foreground">{t('extInt.wizard.schedule')}</legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-xs text-muted-foreground">{t('extInt.wizard.frequency.label')}</span>
                      <select
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={syncFrequency}
                        onChange={(e) => setSyncFrequency(e.target.value as SyncFrequency)}
                        data-testid="wizard-frequency"
                      >
                        {FREQUENCIES.map((f) => (
                          <option key={f} value={f}>{t(`extInt.wizard.frequency.${f}`)}</option>
                        ))}
                      </select>
                    </label>
                    {hasTimeOfDay && (
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">{t('extInt.wizard.timeOfDay')}</span>
                        <TimeField
                          value={syncTimeOfDay}
                          onChange={(v) => setSyncTimeOfDay(v)}
                          data-testid="wizard-time"
                        />
                      </label>
                    )}
                  </div>
                </fieldset>
              ) : (
                <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground" data-testid="wizard-push-note">
                  {t('extInt.wizard.method.webhookPush.noSchedule')}
                </p>
              )}

              {/* The approval gate is its own question. It used to be a third "method", which forced
                  anyone needing written sign-off onto the API transport and made a signed file feed
                  impossible to express. */}
              <label
                className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                  requiresSignedExchange ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/30'
                }`}
                data-testid="wizard-require-signature"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={requiresSignedExchange}
                  onChange={(e) => setRequiresSignedExchange(e.target.checked)}
                />
                <span>
                  <span className={`block text-sm font-medium ${requiresSignedExchange ? 'text-foreground' : 'text-foreground'}`}>
                    {t('extInt.wizard.requireSignature')}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {t('extInt.wizard.requireSignature.body')}
                  </span>
                </span>
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="mt-4 space-y-3">
              <p
                className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
                data-testid="wizard-credentials-intro"
              >
                {t(`extInt.wizard.credentials.${method}.intro`, { system: t(`extInt.system.${system}`) })}
              </p>

              {method === 'fileFeed' ? (
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-foreground">{t('extInt.wizard.fileLocation')}</span>
                  <input
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={fileLocation}
                    onChange={(e) => setFileLocation(e.target.value)}
                    placeholder="sftp://files.example.com/mowazaf/employees"
                    data-testid="wizard-file-location"
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {t('extInt.wizard.fileLocation.hint')}
                  </span>
                </label>
              ) : (
                <>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-foreground">{t('extInt.wizard.tenantUrl')}</span>
                    <input
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={tenantUrl}
                      onChange={(e) => setTenantUrl(e.target.value)}
                      /* Same value the field is pre-filled with, so clearing it leaves the expected
                         shape visible instead of an empty box. */
                      placeholder={getSystemFixture(system)?.defaultTenantUrl}
                      data-testid="wizard-tenant-url"
                    />
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {t('extInt.wizard.tenantUrl.hint', { system: t(`extInt.system.${system}`) })}
                    </span>
                  </label>

                  {method === 'apiPull' && (
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-foreground">{t('extInt.wizard.clientId')}</span>
                      <input
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        placeholder={EXAMPLE_CLIENT_ID}
                        data-testid="wizard-client-id"
                      />
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {t('extInt.wizard.clientId.hint')}
                      </span>
                    </label>
                  )}

                  {method === 'webhookPush' && (
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-foreground">{t('extInt.wizard.callbackUrl')}</span>
                      <input
                        readOnly
                        className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
                        value={callbackUrl}
                        data-testid="wizard-callback-url"
                      />
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {t('extInt.wizard.callbackUrl.hint')}
                      </span>
                    </label>
                  )}
                </>
              )}

              <p className="text-xs text-muted-foreground" data-testid="wizard-secret-note">
                {t('extInt.wizard.credentials.secretNote')}
              </p>
            </div>
          )}

          {step === 3 && (
            <ul className="mt-4 space-y-1 text-sm text-muted-foreground" data-testid="wizard-review">
              <li>{t('extInt.wizard.displayName')}: {displayName}</li>
              <li>{t('extInt.connection.method')}: {t(`extInt.method.${method}`)}</li>
              {/* The one line management asked for: when does this data actually change. */}
              <li data-testid="wizard-review-freshness">{t('extInt.connection.freshness')}: {freshnessSummary}</li>
              <li>
                {t('extInt.connection.target')}: {method === 'fileFeed' ? fileLocation : tenantUrl}
              </li>
              <li data-testid="wizard-review-signature">
                {t('extInt.wizard.requireSignature')}: {requiresSignedExchange ? t('extInt.wizard.requireSignature.on') : t('extInt.wizard.requireSignature.off')}
              </li>
              <li>{t('extInt.config.scopes')}: {(getSystemFixture(system)?.scopes ?? []).join(', ')}</li>
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="wizard-cancel">
            {t('common.cancel')}
          </Button>
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={busy} data-testid="wizard-back">
              {t('extInt.wizard.back')}
            </Button>
          )}
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canAdvance} data-testid="wizard-next">
              {t('extInt.wizard.next')}
            </Button>
          ) : (
            <Button onClick={() => { void handleFinish(); }} disabled={busy} data-testid="wizard-finish">
              {busy && <Spinner size="sm" className="me-2" />}
              {t('extInt.wizard.finish')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddSourceWizard;
